import { useEffect, useMemo, useRef, useState } from 'react';
import { supabase } from '../lib/supabase';
import { useSelection } from '../lib/selection';
import type { Task } from '../lib/database.types';

export function CommandK() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [tasks, setTasks] = useState<Task[]>([]);
  const [cursor, setCursor] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const { select } = useSelection();

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen((v) => !v);
      }
      if (e.key === 'Escape' && open) setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    let mounted = true;
    supabase
      .from('tb_tasks')
      .select('*')
      .neq('status', 'cancelled')
      .then(({ data }) => {
        if (mounted) setTasks((data as Task[] | null) ?? []);
      });
    setTimeout(() => inputRef.current?.focus(), 10);
    return () => {
      mounted = false;
    };
  }, [open]);

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return tasks.slice(0, 20);
    return tasks
      .filter(
        (t) =>
          t.title.toLowerCase().includes(q) ||
          (t.description ?? '').toLowerCase().includes(q) ||
          (t.notes ?? '').toLowerCase().includes(q) ||
          t.id.toLowerCase().includes(q),
      )
      .slice(0, 30);
  }, [tasks, query]);

  useEffect(() => {
    if (cursor >= results.length) setCursor(Math.max(0, results.length - 1));
  }, [results.length, cursor]);

  if (!open) return null;

  const choose = (t: Task) => {
    select({ type: 'task', id: t.id });
    setOpen(false);
    setQuery('');
  };

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm grid place-items-start pt-[12vh]"
      onClick={() => setOpen(false)}
    >
      <div
        className="panel shadow-hud w-full max-w-xl mx-4 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="border-b border-border p-3">
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setCursor(0);
            }}
            onKeyDown={(e) => {
              if (e.key === 'ArrowDown') {
                e.preventDefault();
                setCursor((c) => Math.min(c + 1, results.length - 1));
              }
              if (e.key === 'ArrowUp') {
                e.preventDefault();
                setCursor((c) => Math.max(c - 1, 0));
              }
              if (e.key === 'Enter' && results[cursor]) {
                e.preventDefault();
                choose(results[cursor]);
              }
            }}
            placeholder="search tasks…"
            className="w-full bg-transparent text-lg placeholder-dim focus:outline-none font-mono"
          />
        </div>
        <div className="max-h-[60vh] overflow-y-auto">
          {results.length === 0 && (
            <div className="p-6 text-center text-dim text-sm">no matches</div>
          )}
          {results.map((t, i) => (
            <button
              key={t.id}
              onClick={() => choose(t)}
              onMouseEnter={() => setCursor(i)}
              className={
                'w-full text-left px-4 py-2 flex items-center gap-3 border-l-2 ' +
                (i === cursor
                  ? 'bg-panelHi border-amber'
                  : 'border-transparent hover:bg-panelHi')
              }
            >
              <span className={`chip chip-${t.priority} shrink-0`}>{t.priority}</span>
              <span className="text-sm truncate flex-1">{t.title}</span>
              <span className="text-xs text-dim font-mono shrink-0">{t.status}</span>
            </button>
          ))}
        </div>
        <div className="border-t border-border p-2 flex items-center justify-between text-xs text-dim font-mono">
          <span>↑↓ navigate · ↵ open · esc close</span>
          <span>{results.length} results</span>
        </div>
      </div>
    </div>
  );
}
